import type { Guest } from '@mathflat/domain/Guest'
import type {
  AllGradeSubject,
  GradeHigh,
  NumberGradeElementary as Grade,
  Revision,
  SchoolType,
  Semester,
} from '@mathflat/domain/SchoolSystem'
import type { SupportedWorkbookType, Workbook } from '@mathflat/domain/Workbook'
import type { DropdownParams } from '@mathflat/shared/@types/mathflatApi'
import { makeAutoObservable } from 'mobx'

type FlatGrade = Grade | 7 | 8 | 9 | 10 | 11 | 12

export type SchoolGradeType = {
  grade: Grade
  flatGrade: FlatGrade
  schoolType: SchoolType
}

type ConstructorParams = Partial<SchoolGradeType>

// 학년 타입, 학년 정보
export class SchoolGrade {
  static KO_LABEL = [
    '초1',
    '초2',
    '초3',
    '초4',
    '초5',
    '초6',
    '중1',
    '중2',
    '중3',
    '고1',
    '고2',
    '고3',
  ] as const
  schoolType: SchoolGradeType['schoolType'] = 'ELEMENTARY'
  grade: Grade = 1

  constructor({ schoolType = 'ELEMENTARY', grade = 1 }: ConstructorParams = {}) {
    this.schoolType = schoolType
    this.grade = grade
    makeAutoObservable(this)
  }

  get selectableGrade() {
    switch (this.schoolType) {
      case 'ELEMENTARY':
        return [1, 2, 3, 4, 5, 6]
      case 'HIGH':
      case 'MIDDLE':
      default:
        return [1, 2, 3]
    }
  }

  get stringGrade() {
    return `${this.grade}` as AllGradeSubject
  }

  get flatGrade(): FlatGrade {
    const elemGrade: 0 | 6 | 9 = (() => {
      switch (this.schoolType) {
        default:
        case 'ELEMENTARY':
          return 0
        case 'MIDDLE':
          return 6
        case 'HIGH':
          return 9
      }
    })()
    return (elemGrade + this.grade) as FlatGrade
  }

  set flatGrade(val: FlatGrade) {
    const data = fromFlatGrade(val)
    this.schoolType = data.schoolType
    this.grade = data.grade
  }

  get koLabel() {
    return SchoolGrade.KO_LABEL[this.flatGrade - 1]
  }

  static fromFlatGrade(val: FlatGrade) {
    return new this(fromFlatGrade(val))
  }

  static toFlatGrade(grade: number, schoolType: SchoolType) {
    return toFlatGrade(grade, schoolType)
  }

  /**
   * guest.grade 는 숫자 문자열 계약(크레버스 ERP 동기화). 위반 값은 보정하지 않는다 —
   * 임의 학년으로 보정하면 잘못된 학년 그룹에 노출되는 오정보가 되므로, 비숫자는 학년 그룹
   * 누락으로 둔다.
   */
  static fromGuest(guest: Pick<Guest, 'schoolType' | 'grade'>) {
    return new this({ schoolType: guest.schoolType, grade: Number(guest.grade) as Grade })
  }

  static fromKoLabel(label: SchoolGrade['koLabel']) {
    const index = (SchoolGrade.KO_LABEL.findIndex((val) => val === label) + 1) as FlatGrade | -1

    if (index !== -1) {
      return new this(SchoolGrade.fromFlatGrade(index))
    }
  }

  setGrade(grade: Grade) {
    this.grade = grade
  }

  setSchoolType(schoolType: SchoolGradeType['schoolType']) {
    this.grade = 1
    this.schoolType = schoolType
  }

  static getKoreanSchoolType(schoolType: SchoolType): '초' | '중' | '고' {
    switch (schoolType) {
      case 'ELEMENTARY':
        return '초'
      case 'MIDDLE':
        return '중'
      case 'HIGH':
        return '고'
    }
  }

  static getKoreanLongSchoolType(schoolType: SchoolType): '초등학교' | '중학교' | '고등학교' {
    switch (schoolType) {
      case 'ELEMENTARY':
        return '초등학교'
      case 'MIDDLE':
        return '중학교'
      case 'HIGH':
        return '고등학교'
    }
  }

  static getKoreanSchoolGrade(grade: AllGradeSubject, schoolType: SchoolType) {
    switch (schoolType) {
      case 'ELEMENTARY':
        switch (grade) {
          case '1':
            return '초1'
          case '2':
            return '초2'
          case '3':
            return '초3'
          case '4':
            return '초4'
          case '5':
            return '초5'
          case '6':
          default:
            return '초6'
        }
      case 'MIDDLE':
        switch (grade) {
          case '1':
            return '중1'
          case '2':
            return '중2'
          case '3':
          default:
            return '중3'
        }
      case 'HIGH':
        switch (grade) {
          case '1':
            return '고1'
          case '2':
            return '고2'
          case '3':
          default:
            return '고3'
        }
    }
  }

  static getKoreanWorksheetGradeSubject(option: {
    grade: AllGradeSubject
    schoolType: SchoolType
  }) {
    // 초등, 중등
    if (!isNaN(Number(option.grade))) {
      return SchoolGrade.getKoreanSchoolGrade(option.grade, option.schoolType)
    } else {
      return SchoolGrade.getKoreanHighSubjectShort(option.grade as GradeHigh)
    }
  }

  static getKoreanHighSubjectShort(subject: GradeHigh | string) {
    if (Number(subject)) return `고${subject}`
    switch (subject) {
      case '고등수학(상)':
        return '수학(상)'
      case '고등수학(하)':
        return '수학(하)'
      case '확률과 통계':
        return '확통'
      default:
        return subject
    }
  }

  static getKoreanCommonGradeSemester(option: {
    grade: AllGradeSubject
    semester: Semester
    schoolType: SchoolType
  }) {
    const koSchoolType = SchoolGrade.getKoreanSchoolType(option.schoolType)
    const koGrade = this.getKoreanSchoolGrade(option.grade, option.schoolType)
    switch (option.schoolType) {
      case 'ELEMENTARY':
      case 'MIDDLE':
        return {
          koSchoolType,
          koGrade,
          grade: option.grade,
          semester: option.semester,
          koSubject: null,
        }
      case 'HIGH':
      default:
        return {
          koSchoolType,
          koGrade,
          grade: null,
          semester: null,
          koSubject: SchoolGrade.getKoreanHighSubjectShort(option.grade as GradeHigh),
        }
    }
  }

  // 교재 학년 - 학기 정보
  static getKoreanWorkbookGradeSemester({
    type,
    schoolType,
    grade,
    semester = 1,
    onlyGrade = false,
  }: {
    type: Workbook['type']
    schoolType: SchoolType
    grade: AllGradeSubject
    semester?: Semester
    onlyGrade?: boolean
  }) {
    const { koSchoolType, koSubject } = SchoolGrade.getKoreanCommonGradeSemester({
      grade,
      semester,
      schoolType,
    })

    if ((schoolType === 'MIDDLE' && type === 'SCHOOL') || onlyGrade) {
      return `${koSchoolType}${grade}`
    }

    if (koSchoolType === '고') {
      return koSubject
    }

    return `${koSchoolType} ${grade}-${semester}`
  }

  static getKoreanWorkbookGradeSemesterRevision({
    type,
    schoolType,
    grade,
    semester = 1,
    onlyGrade = false,
    revision = 'CURRICULUM_15',
  }: {
    type: DropdownParams<SupportedWorkbookType>
    schoolType: SchoolType
    grade: AllGradeSubject
    semester?: Semester
    onlyGrade?: boolean
    revision?: Revision
  }) {
    const { koSchoolType, koSubject } = SchoolGrade.getKoreanCommonGradeSemester({
      grade,
      semester,
      schoolType,
    })

    if ((schoolType === 'MIDDLE' && type === 'SCHOOL') || onlyGrade) {
      return `${koSchoolType}${grade}`
    }

    if (koSchoolType === '고') {
      return koSubject
    }

    return `${koSchoolType} ${grade}-${semester} ${revision === 'CURRICULUM_22' ? '(22개정)' : ''}`
  }
}

function toFlatGrade(grade: number, schoolType: SchoolType) {
  switch (schoolType) {
    case 'ELEMENTARY':
      return grade
    case 'MIDDLE':
      return grade + 6
    case 'HIGH':
      return grade + 9
  }
}

function fromFlatGrade(val: FlatGrade) {
  if (val > 9) {
    return {
      schoolType: 'HIGH' as SchoolType,
      grade: (val - 9) as Grade,
    }
  }

  if (val > 6) {
    return {
      schoolType: 'MIDDLE' as SchoolType,
      grade: (val - 6) as Grade,
    }
  }

  return {
    schoolType: 'ELEMENTARY' as SchoolType,
    grade: val as Grade,
  }
}
